import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { fetchApi } from "@/helpers/h";
import * as Sentry from "@sentry/react";
import { useData } from "@/stores/data";

export function useApiMutations(resourcePath) {
  const navigate = useNavigate();
  const { setTriggers } = useData();

  const mutationFn = async ({
    method,
    suffix = "",
    payload,
    isFormData = false,
  }) => {
    const res = await fetchApi({
      url: `${resourcePath}${suffix}`,
      method: method,
      data: payload,
      contentType: isFormData ? "multipart/form-data" : "application/json",
    });
    return res;
  };

  const mutation = useMutation({
    mutationFn,
    onSuccess: (res, variables) => {
      if (variables?.onSuccess) {
        variables.onSuccess(res?.data);
      }
      setTriggers();
    },
    onError: (err, variables) => {
      Sentry.captureException(err, {
        tags: {
          resourcePath: resourcePath,
          errorType: "api_mutation",
        },
        extra: {
          method: variables?.method,
          suffix: variables?.suffix,
          errorMessage: err?.message,
          status: err?.response?.status,
        },
      });

      if (err?.response?.status === 401) {
        localStorage.removeItem("user");
        navigate("/auth/login");
        return;
      }
      if (variables?.onError) {
        variables.onError(err?.message ?? "Something went wrong");
      }
    },
  });

  return {
    isLoading: mutation.isPending,
    error: mutation.error,
    create: (payload, opts) =>
      mutation.mutate({ method: "POST", suffix: "", payload, ...opts }),
    update: (id, payload, opts) =>
      mutation.mutate({ method: "PUT", suffix: `/${id}`, payload, ...opts }),
    delete: (id, opts) =>
      mutation.mutate({
        method: "DELETE",
        suffix: `/${id}`,
        payload: null,
        ...opts,
      }),
    post: (suffix, payload, opts) =>
      mutation.mutate({ method: "POST", suffix, payload, ...opts }),
  };
}
